import styled from '@emotion/styled'
import { AnimatedLine } from 'app/components/Common/Animation/AnimatedLine'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { theme } from 'app/theme/index'
import { rgba } from 'emotion-rgba'
import React, { memo, useEffect } from 'react'
import { isMobile } from 'react-device-detect'

import { Item } from './Item'

export interface Props {
  leftCta?: ButtonProps
  rightCta?: ButtonProps
  description?: string
  languageCode: string
  list?: any[]
  title?: string
  variant?: Variant
}

export const Info = memo(function Info({
  leftCta,
  rightCta,
  languageCode,
  list,
  title,
  variant = 'default',
}: Props) {
  if (!title) {
    return null
  }

  {
    variant == 'restaurant'
      ? useEffect(() => {
          const script = document.createElement('script')
          script.async = true

          script.src = `https://s3-eu-west-1.amazonaws.com/quandoo-website/widget-builder/quandoo-widget-builder.js`

          document.body.appendChild(script)

          return () => {
            document.body.removeChild(script)
          }
        })
      : null
  }

  let lang: string = 'en'
  let bookLabel: string = 'Book a table'

  switch (languageCode) {
    case 'en-US':
      lang = 'en'
      bookLabel = 'Book a table'
      break
    case 'it-IT':
      lang = 'it'
      bookLabel = 'Prenota un tavolo'
      break

    default:
      break
  }

  return (
    <Container variant={variant}>
      <AnimatedLine variant="light" />
      <Wrapper dial={1} row wrap space="between">
        <LeftSide>{title ? <Title tag="h2" text={title} /> : null}</LeftSide>
        <RightSide>
          {list ? (
            <List row wrap>
              {list.map((item, index) => (
                <FadeInUp delay={index * 0.1} key={index}>
                  <Item {...item} />
                </FadeInUp>
              ))}
            </List>
          ) : null}

          {leftCta || rightCta || variant === 'restaurant' ? (
            <Buttons dial={1} row wrap>
              {variant == 'restaurant' ? (
                <FadeInUp>
                  <div
                    className="quandoo-widget-builder"
                    data-config={`{"format":"text-button","lang":"${lang}","bgcolor":"transparent","txcolor":"#ffffff","round":"yes","position":"","font":"md","merchant":91989,"txt":"${bookLabel}"}`}
                  ></div>
                </FadeInUp>
              ) : null}
              {leftCta ? (
                <FadeInUp>
                  <CTA
                    variant="white"
                    {...leftCta}
                    onClick={() => {
                      if (isMobile && variant == 'restaurant') {
                        typeof window.gtag !== 'undefined' &&
                          window.gtag('event', 'Click', {
                            event_category: 'Website',
                            event_label: 'Call Restaurant',
                          })
                      }
                    }}
                  />
                </FadeInUp>
              ) : null}
              {rightCta ? (
                <FadeInUp>
                  <CTA variant="white" {...rightCta} />
                </FadeInUp>
              ) : null}
            </Buttons>
          ) : null}
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section<ContainerProps>`
  text-align: center;
  position: relative;
`

const Wrapper = styled(FlexBox)`
  padding: 4.4375rem 13.472vw 0;
  margin: 0 auto 13.125rem;

  @media (max-width: 1199px) {
    padding: 3rem 3rem 0;
    margin: 0 auto 3rem;
  }
`

const LeftSide = styled.div`
  width: 50%;
  max-width: 15.625rem;
  text-align: left;
  margin-right: auto;

  @media (max-width: 1199px) {
    width: 100%;
    max-width: none;
    text-align: center;
  }
`

const RightSide = styled.div`
  width: 50%;
  padding-left: 1.5rem;

  @media (max-width: 1199px) {
    width: 100%;
    padding-left: 0;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  @media (max-width: 1199px) {
    margin-bottom: 2.25rem;
  }
`

const List = styled(FlexBox)`
  margin: 0 auto;
  text-align: left;

  > div {
    margin-top: 3rem;

    &:first-of-type {
      margin-top: 0;
    }

    @media (max-width: 1199px) {
      text-align: center;
      margin-top: 2.25rem;
    }
  }

  @media (max-width: 1199px) {
    justify-content: center;
  }
`

const Buttons = styled(FlexBox)`
  margin-top: 3rem;

  > div {
    margin-right: 0.75rem;
    margin-bottom: 0.75rem;

    &:last-of-type a {
      margin-right: 0;
    }
  }

  @media (max-width: 1199px) {
    > div {
      width: 100%;

      &:last-of-type a {
        margin-top: 0 !important;
      }
    }
  }

  .quandoo-widget-builder {
    .quandoo-button.quandoo-button-text {
      display: inline-block;
      box-shadow: none;
      background: ${({ theme }) => theme.colors.variants.primaryLight};
      color: ${({ theme }) => theme.colors.variants.neutralLight4};
      cursor: pointer;
      font-family: ${({ theme }) => theme.fontFamily.heading};
      font-size: 0.875rem;
      letter-spacing: 0.05em;
      border-radius: 3rem;
      line-height: 1.125rem;
      padding: 0.75rem 1.5rem;
      text-transform: uppercase;
      transition: 0.5s ease-in-out;
      background: none;
      border: 0.0625rem solid ${rgba(theme.colors.variants.neutralLight4, 0.3)};
      color: ${theme.colors.variants.neutralLight4};
      &:hover {
        border-color: ${rgba(theme.colors.variants.neutralLight4, 0.6)};

        span {
          opacity: 1;
        }
      }

      @media (max-width: 574px) {
        padding: 1rem 2rem 1rem 2rem;
      }
    }
  }

  @media (max-width: 1199px) {
    margin-top: 2.25rem;
    flex-direction: column;
    align-items: center;

    > div:last-of-type a {
      margin-left: 0;
      margin-top: 0.75rem;
    }
  }
`

const CTA = styled(Button)``

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'restaurant'
